<script lang="ts" setup>
import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';

interface Props {
  variant?: 'none' | 'bullet' | 'numbered';
  gap?: boolean;
  icon?: 'none' | 'tick';
  iconColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'none',
  gap: false,
  icon: 'none',
  iconColor: 'text-success-foreground',
});

const slots = useSlots();

const listChildren = computed(() => {
  return slots.default?.()?.[0]?.children ?? [];
});

const listClass = computed(() => {
  const showIcon = props.icon !== 'none';

  return twMerge(
    cva('', {
      variants: {
        variant: {
          none: '',
          bullet: 'list-outside list-disc pl-4',
          numbered: 'list-outside list-decimal pl-4',
        },
      },
    })({
      variant: showIcon ? 'none' : props.variant,
    }),
  );
});

const liClass = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        gap: {
          true: 'flex flex-wrap gap-2',
          false: '',
        },
      },
    })({
      gap: props.gap,
    }),
  );
});

const showTickIcon = computed(() => props.icon === 'tick');

const listSemanticTag = computed(() => (props.variant === 'numbered' ? 'ol' : 'ul'));
</script>

<template>
  <component :is="listSemanticTag" :class="listClass">
    <li
      v-for="(child, index) in listChildren"
      :key="index"
      :class="[liClass, { 'flex items-start': showTickIcon }]"
    >
      <Icon
        v-if="showTickIcon"
        :name="ICONS.tick"
        size="xs"
        class="mt-1.5 mr-1.5 shrink-0"
        :class="[iconColor]"
        aria-hidden="true"
      />
      <span v-if="showTickIcon" class="leading-relaxed">
        <component :is="child" />
      </span>
      <component :is="child" v-else />
    </li>
  </component>
</template>
